import React, { useState } from 'react';
import apiService from '../services/api';
import PetSetup from './PetSetup';
import ClientPortal from './ClientPortal';
import TrainerSetup from './TrainerSetup';

const LoginPage = ({ onBackToHome, onLoginSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [currentFlow, setCurrentFlow] = useState('auth'); // 'auth', 'petSetup', 'trainerSetup', 'trainerDisclaimer', 'clientPortal'
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    userType: '',
    username: '' // Kept for registration API compatibility
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [verificationStep, setVerificationStep] = useState('none'); // 'none', 'requested', 'verified'
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationToken, setVerificationToken] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePetSetupComplete = () => {
    // Store client ID for the portal
    localStorage.setItem('clientId', userData.user_id);
    // Redirect to client portal
    onLoginSuccess('client');
  };

  const handleTrainerSetupComplete = () => {
    // Redirect to trainer portal
    onLoginSuccess('trainer');
  };

  const handleLogout = () => {
    setCurrentFlow('auth');
    setUserData(null);
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      name: '',
      userType: '',
      username: ''
    });
  };

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 10) {
      errors.push('Password must be at least 10 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return errors;
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError('Email and password are required');
      return false;
    }

    if (!isLogin) {
      if (!formData.email) {
        setError('Email is required');
        return false;
      }
      if (!formData.userType) {
        setError('Please select whether you are a client or trainer');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
      
      // Validate password complexity
      const passwordErrors = validatePassword(formData.password);
      if (passwordErrors.length > 0) {
        setError(passwordErrors.join('. '));
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('=== FORM SUBMITTED ===');
    console.log('Form data:', formData);
    console.log('Is login:', isLogin);
    
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (isLogin) {
        console.log('Attempting login...');
        // Login - use email as username for backend compatibility
        const response = await apiService.login(formData.email, formData.password);
        
        // Store token for future use
        localStorage.setItem('token', response.access_token);
        localStorage.setItem('user_id', response.user_id);
        localStorage.setItem('username', response.username);
        
        // Store user data for potential profile completion
        setUserData({
          user_id: response.user_id,
          username: response.username,
          user_type: response.user_type,
          email: response.email
        });
        
        // Store client/trainer ID based on user type
        console.log('=== LOGIN RESPONSE ===');
        console.log('Response:', response);
        console.log('User type:', response.user_type);
        console.log('User ID:', response.user_id);
        
        // Store email for later use
        localStorage.setItem('email', response.email || formData.email);
        
        if (response.user_type === 'client') {
          console.log('Setting clientId to:', response.user_id);
          localStorage.setItem('clientId', response.user_id);
        } else if (response.user_type === 'trainer') {
          // For trainers, we need to get the actual trainer ID from the trainer database
          try {
            const trainerResponse = await apiService.getTrainerID(response.email);
            if (trainerResponse.Code === 200) {
              localStorage.setItem('trainerId', trainerResponse.id);
            } else {
              console.error('Failed to get trainer ID:', trainerResponse.Message);
              // Fallback to user_id if trainer ID not found
              localStorage.setItem('trainerId', response.user_id);
            }
          } catch (err) {
            console.error('Error getting trainer ID:', err);
            // Fallback to user_id if error occurs
            localStorage.setItem('trainerId', response.user_id);
          }
        }
        
        // Redirect based on user status and profile completion
        if (!response.user_type) {
          // User needs to select type - redirect to trainer selection for now
          // In a full implementation, you'd redirect to user type selection
          onLoginSuccess('client');
        } else if (response.user_type === 'trainer' && !response.profile_completed) {
          // Trainer needs to complete profile setup
          onLoginSuccess('trainer-setup');
        } else {
          // User is ready - redirect to appropriate portal
          onLoginSuccess(response.user_type);
        }
      } else {
        // Register - check if email verification is required
        if (verificationStep === 'none') {
          // Step 1: Check if email verification is required
          setLoading(true);
          setError(null);
          try {
            const verificationResponse = await apiService.requestEmailVerification(formData.email, formData.userType);
            
            // Check if verification is required
            if (verificationResponse.verification_required === false) {
              // Email verification not required - proceed directly to registration
              setVerificationStep('skipped');
              // Continue to registration without token
              const response = await apiService.register(
                formData.email, 
                formData.email, 
                formData.password, 
                formData.name, 
                formData.userType,
                null // No verification token needed
              );
              
              // Store user data
              setUserData({
                user_id: response.user_id,
                username: response.username,
                user_type: response.user_type,
                email: formData.email
              });
              
              setError(null);
              
              // Redirect based on user type
              if (response.user_type === 'client') {
                setCurrentFlow('petSetup');
              } else if (response.user_type === 'trainer') {
                setCurrentFlow('trainerDisclaimer');
              } else {
                onLoginSuccess(response.user_type);
              }
              setLoading(false);
              return;
            } else {
              // Email verification is required
              setVerificationStep('requested');
              setError(null);
            }
          } catch (err) {
            console.error('Email verification request error:', err);
            setError(err.message || 'Failed to send verification code. Please try again.');
          } finally {
            setLoading(false);
          }
          return;
        } else if (verificationStep === 'requested') {
          // Step 2: Verify the code
          if (!verificationCode || verificationCode.length !== 6) {
            setError('Please enter the 6-digit verification code');
            return;
          }
          setLoading(true);
          setError(null);
          try {
            const verifyResponse = await apiService.verifyEmailCode(formData.email, verificationCode);
            if (verifyResponse.verification_token) {
              setVerificationToken(verifyResponse.verification_token);
              setVerificationStep('verified');
              setError(null);
            } else {
              setError('Failed to verify code. Please try again.');
            }
          } catch (err) {
            console.error('Email verification error:', err);
            setError(err.message || 'Invalid verification code. Please try again.');
          } finally {
            setLoading(false);
          }
          return;
        } else if (verificationStep === 'verified' && verificationToken) {
          // Step 3: Complete registration with verification token
          console.log('Registration data:', {
            username: formData.email,
            email: formData.email,
            password: formData.password,
            name: formData.name,
            userType: formData.userType,
            verificationToken: verificationToken
          });
          
          const response = await apiService.register(
            formData.email, 
            formData.email, 
            formData.password, 
            formData.name, 
            formData.userType,
            verificationToken
          );
          
          // Store user data
          setUserData({
            user_id: response.user_id,
            username: response.username,
            user_type: response.user_type,
            email: formData.email
          });
          
          setError(null);
          
          // Redirect based on user type
          if (response.user_type === 'client') {
            setCurrentFlow('petSetup');
          } else if (response.user_type === 'trainer') {
            setCurrentFlow('trainerDisclaimer');
          } else {
            // For other types, go directly to selection
            onLoginSuccess(response.user_type);
          }
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.message || (isLogin ? 'Login failed' : 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      setError('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await apiService.forgotPassword(resetEmail);
      
      setResetSuccess(true);
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetEmail('');
        setResetSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '10px',
      padding: '40px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      maxWidth: '400px',
      width: '100%'
    },
    backButton: {
      background: 'transparent',
      color: '#2c3e50',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      marginBottom: '20px',
      textDecoration: 'underline'
    },
    logo: {
      fontSize: '48px',
      marginBottom: '20px',
      textAlign: 'center'
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      marginBottom: '10px',
      color: '#333',
      textAlign: 'center'
    },
    subtitle: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '30px',
      textAlign: 'center'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px'
    },
    input: {
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '5px',
      fontSize: '16px'
    },
    button: {
      padding: '15px',
      backgroundColor: '#e67e22',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
      marginTop: '10px'
    },
    buttonDisabled: {
      backgroundColor: '#6c757d',
      cursor: 'not-allowed'
    },
    forgotPasswordLink: {
      backgroundColor: 'transparent',
      color: '#94a3b8',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.875rem',
      textAlign: 'center',
      marginTop: '8px',
      marginBottom: '8px',
      textDecoration: 'none'
    },
    toggleButton: {
      backgroundColor: 'transparent',
      color: '#e67e22',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      textAlign: 'center',
      marginTop: '10px',
      textDecoration: 'underline'
    },
    error: {
      color: 'red',
      fontSize: '14px',
      marginTop: '10px',
      textAlign: 'center'
    },
    disclaimerBox: {
      backgroundColor: '#fff3cd',
      border: '2px solid #ffc107',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '30px',
      textAlign: 'left'
    },
    disclaimerText: {
      fontSize: '16px',
      color: '#856404',
      lineHeight: '1.6',
      marginBottom: '15px'
    },
    primaryButton: {
      padding: '15px',
      backgroundColor: '#e67e22',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
      width: '100%',
      marginTop: '10px'
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modal: {
      background: '#1e293b',
      borderRadius: '12px',
      padding: '30px',
      minWidth: '400px',
      maxWidth: '500px',
      border: '1px solid #334155'
    },
    modalTitle: {
      color: '#f1f5f9',
      fontSize: '1.5rem',
      fontWeight: '600',
      marginBottom: '16px'
    },
    modalDescription: {
      color: '#94a3b8',
      fontSize: '0.9375rem',
      marginBottom: '20px'
    },
    modalActions: {
      display: 'flex',
      gap: '12px',
      marginTop: '20px',
      justifyContent: 'flex-end'
    },
    cancelButton: {
      padding: '10px 20px',
      background: 'transparent',
      color: '#94a3b8',
      border: '1px solid #475569',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.9375rem'
    },
    successMessage: {
      padding: '20px',
      background: 'rgba(34, 197, 94, 0.1)',
      borderRadius: '8px',
      border: '1px solid #22c55e',
      marginTop: '16px'
    },
    successText: {
      color: '#22c55e',
      fontSize: '0.9375rem',
      margin: 0
    },
    loading: {
      color: '#e67e22',
      fontSize: '14px',
      marginTop: '10px',
      textAlign: 'center'
    },
    passwordRequirements: {
      fontSize: '12px',
      color: '#666',
      marginTop: '5px'
    },
    verificationBox: {
      backgroundColor: '#e8f5e9',
      border: '1px solid #4caf50',
      borderRadius: '8px',
      padding: '15px',
      marginTop: '15px'
    },
    verificationText: {
      fontSize: '14px',
      color: '#2e7d32',
      marginBottom: '10px',
      textAlign: 'center'
    },
    resendButton: {
      backgroundColor: 'transparent',
      color: '#4caf50',
      border: '1px solid #4caf50',
      borderRadius: '5px',
      padding: '8px 15px',
      fontSize: '14px',
      cursor: 'pointer',
      marginTop: '10px',
      width: '100%'
    }
  };

  // Handle different flows
  if (currentFlow === 'petSetup') {
    return (
      <PetSetup 
        onComplete={handlePetSetupComplete}
        onBackToHome={onBackToHome}
        clientId={userData?.user_id}
      />
    );
  }

  if (currentFlow === 'trainerDisclaimer') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <button onClick={onBackToHome} style={styles.backButton}>
            ← Back to Home
          </button>
          
          <div style={styles.logo}>🐕‍🦺</div>
          <h1 style={styles.title}>Trainer Registration Requirements</h1>
          
          <div style={styles.disclaimerBox}>
            <p style={styles.disclaimerText}>
              Before beginning the sign up process, you must have already added Pheefee as part of your insurance rider and you must be willing to undergo a background check.
            </p>
            <p style={styles.disclaimerText}>
              There will be a one-time charge of <strong>$50</strong> in order to register as a trainer.
            </p>
          </div>
          
          <button
            onClick={() => setCurrentFlow('trainerSetup')}
            style={styles.primaryButton}
          >
            I Understand, Continue
          </button>
        </div>
      </div>
    );
  }

  if (currentFlow === 'trainerSetup') {
    return (
      <TrainerSetup 
        onSetupComplete={handleTrainerSetupComplete}
        onBackToHome={onBackToHome}
        userData={userData}
      />
    );
  }

  if (currentFlow === 'clientPortal') {
    return (
      <ClientPortal 
        onBackToHome={onBackToHome}
        onLogout={handleLogout}
        isLoggedIn={true}
      />
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <button onClick={onBackToHome} style={styles.backButton}>
          ← Back to Home
        </button>
        
        <div style={styles.logo}>🐕‍🦺</div>
        <h1 style={styles.title}>Dog Training On Demand</h1>
        <p style={styles.subtitle}>
          {isLogin ? 'Welcome back!' : 'Join our community'}
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleInputChange}
            style={styles.input}
            required
          />

          {!isLogin && (
            <>
              <input
                type="text"
                name="name"
                placeholder="Full Name (optional)"
                value={formData.name}
                onChange={handleInputChange}
                style={styles.input}
              />
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  color: '#2c3e50',
                  marginBottom: '10px',
                  fontSize: '1rem'
                }}>
                  I am a: *
                </label>
                <div style={{ display: 'flex', gap: '15px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '10px 15px',
                    border: `2px solid ${formData.userType === 'client' ? '#e67e22' : '#e2e8f0'}`,
                    borderRadius: '8px',
                    backgroundColor: formData.userType === 'client' ? '#fef7f0' : 'white',
                    flex: 1,
                    justifyContent: 'center'
                  }}>
                    <input
                      type="radio"
                      name="userType"
                      value="client"
                      checked={formData.userType === 'client'}
                      onChange={handleInputChange}
                      style={{ margin: 0 }}
                    />
                    <span>🐕 Client (Need Training)</span>
                  </label>
                  
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '10px 15px',
                    border: `2px solid ${formData.userType === 'trainer' ? '#e67e22' : '#e2e8f0'}`,
                    borderRadius: '8px',
                    backgroundColor: formData.userType === 'trainer' ? '#fef7f0' : 'white',
                    flex: 1,
                    justifyContent: 'center'
                  }}>
                    <input
                      type="radio"
                      name="userType"
                      value="trainer"
                      checked={formData.userType === 'trainer'}
                      onChange={handleInputChange}
                      style={{ margin: 0 }}
                    />
                    <span>🎓 Trainer (Provide Training)</span>
                  </label>
                </div>
              </div>
            </>
          )}

          <input
            type="password"
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleInputChange}
            style={styles.input}
            required
          />

          {!isLogin && (
            <>
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                style={styles.input}
                required
              />
              <div style={styles.passwordRequirements}>
                Password must be at least 10 characters with uppercase, lowercase, and special character
              </div>
              
              {/* Email Verification Step */}
              {verificationStep === 'requested' && (
                <div style={styles.verificationBox}>
                  <p style={styles.verificationText}>
                    We've sent a verification code to <strong>{formData.email}</strong>
                  </p>
                  <input
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setVerificationCode(value);
                    }}
                    style={styles.input}
                    maxLength={6}
                    required
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      setLoading(true);
                      setError(null);
                      try {
                        await apiService.requestEmailVerification(formData.email, formData.userType);
                        setError(null);
                        alert('Verification code resent!');
                      } catch (err) {
                        setError(err.message || 'Failed to resend code. Please try again.');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    style={styles.resendButton}
                  >
                    Resend Code
                  </button>
                </div>
              )}
            </>
          )}

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {})
            }}
            disabled={loading}
          >
            {loading ? 'Please wait...' : (
              isLogin ? 'Login' : (
                verificationStep === 'requested' ? 'Verify Code' :
                verificationStep === 'verified' ? 'Complete Registration' :
                'Send Verification Code'
              )
            )}
          </button>
        </form>

        {isLogin && (
          <button
            onClick={() => setShowForgotPassword(true)}
            style={styles.forgotPasswordLink}
          >
            Forgot your password?
          </button>
        )}

        <button
          onClick={() => {
            setIsLogin(!isLogin);
            setError(null);
            setVerificationStep('none');
            setVerificationCode('');
            setVerificationToken(null);
            setFormData({
              email: '',
              password: '',
              confirmPassword: '',
              name: '',
              userType: '',
              username: ''
            });
          }}
          style={styles.toggleButton}
        >
          {isLogin ? "Don't have an account? Sign Up" : 'Already have an account? Login'}
        </button>

        {error && <div style={styles.error}>{error}</div>}
        {loading && <div style={styles.loading}>Processing...</div>}
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Reset Password</h2>
            {resetSuccess ? (
              <div style={styles.successMessage}>
                <p style={styles.successText}>
                  ✓ Password reset email sent! Check your inbox for instructions.
                </p>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} style={styles.form}>
                <p style={styles.modalDescription}>
                  Enter your email address and we'll send you instructions to reset your password.
                </p>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  style={styles.input}
                  required
                />
                <div style={styles.modalActions}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail('');
                      setError(null);
                    }}
                    style={styles.cancelButton}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={styles.submitButton}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
